import React, { useEffect, useState } from "react";
import Api from "../../services/Api";
import StatusHelper from "../../helpers/StatusHelper";
import Notifications from "../../services/Notifications";
import PDFIcon from "../icons/OrderAckIcon";
import { GridToolbarContainer, GridToolbarFilterButton, GridToolbarExport, GridToolbarDensitySelector } from '@mui/x-data-grid-premium';
import DateDisplay from "../DateDisplay";
import TranslatableDataGridServerLoaded from '../shared/TranslatableDataGridServerLoaded';
import StorefrontAdminTheme from '../shared/MUIStyles/StorefrontAdminTheme';
import { useStorefrontAdministrationConfig } from '../../hooks/StorefrontAdministrationConfigContext';
import QuoteService from "../../services/QuoteService";
import { Portal, Box, Stack, Typography, Chip, IconButton, Button, Divider, SwipeableDrawer, Paper, ThemeProvider, Fade } from '@mui/material';
import { Link, withRouter } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ModalPopup from "../../services/ModalPopup";
import { useCartConfig } from '../../hooks/CartConfigContext';
import CircularProgress from "@mui/material/CircularProgress";
import ConvertToOrderModal from '../quotes/ConvertToOrderModal';


const QuoteLines = (props) => {
    const localized = props?.localized;
    const cartOptions = useCartConfig();
    const [rows, setRows] = useState([]);
    const [convertingQuote, setConvertingQuote] = useState(false);
    const [convertedQuote, setConvertedQuote] = useState(false);
    const [documentNumber, setDocumentNumber] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const selectedCustomer = props.selectedCustomer ? props.selectedCustomer : null;
    const [requiredPO, setRequiredPO] = useState((cartOptions.childComponents.PONumber ? cartOptions.childComponents.PONumber.shown && cartOptions.childComponents.PONumber.required : false) === true || (selectedCustomer && selectedCustomer.customerPORequired === "Y") || false);
    const [openConvertModal, setOpenConvertModal] = useState(false);   
    const [rejecting, setRejecting] = useState(false);
    const [rejectingDocument, setRejectingDocument] = useState(null);
    const customerId = selectedCustomer ? selectedCustomer.id : null;
    const implementationId = props.storefrontConfig.storefront.implementationId;
    const documentCompany = selectedCustomer ? selectedCustomer.companyCode : null;

    const [convertPO, setConvertPO] = useState("");
    const [convertComment, setConvertComment] = useState("");
    const [convertModalFetching, setConvertModalFetching] = useState(false);
    const poNumberUpdated = (value) => setConvertPO(value);
    const commentsUpdated = (value) => setConvertComment(value);
    const handleCloseConvertModal = () => setOpenConvertModal(false);

    const [confirmationOptions, setConfirmationOptions] = useState({
        show: false,
        message: "",
    });
   
    const wrapperTheme = StorefrontAdminTheme;
    const gridLocalized = useStorefrontAdministrationConfig().labels;
    const isAnyModalOpen = confirmationOptions.show || openConvertModal;
    const [drawerOpen, setDrawerOpen] = useState(false);


    const openDrawer = (row) => {
        setSelectedRow(row);
        setDrawerOpen(true);
    };

    const closeDrawer = () => setDrawerOpen(false);

    useEffect(() => {
        if (!openConvertModal) {
            getQuotes();
        }
    }, [props.quotes, openConvertModal]);

    const getQuotes = () => {
        if (props.quotes === null) {
            props.fetchOrders();
        }
        if (props.quotes) {
            mapOrderlinesToOrders(props.quotes);
        }
    }

    const customToolbar = () => {
        return (
            <GridToolbarContainer  >
                <Box sx={{ flexGrow: 1 }} />
                <GridToolbarFilterButton
                    slotProps={{
                        tooltip: { title: 'Filter list' }
                    }}
                    variant="outlined"
                    color="update"

                />
                <GridToolbarDensitySelector
                    slotProps={{
                        tooltip: { title: 'Change density' }
                    }}
                    variant="outlined"
                    color="update"
                />
                <GridToolbarExport
                    printOptions={{ disableToolbarButton: true }}
                    
                    csvOptions={{ fields: ['submittedOn', 'quoteNumber', 'status', 'poNumber'] }}    
                    excelOptions={{ fields: ['submittedOn', 'quoteNumber', 'status', 'poNumber'] }}   

                    slotProps={{
                        tooltip: { title: 'Export data' }
                    }}
                    variant="outlined"
                    color="update"
                />
            </GridToolbarContainer>
        );
    }

    const mapOrderlinesToOrders = (quoteLines) => {
        var rows = [];
        quoteLines.forEach((quoteLine, index) => {
            rows.push({
                id: index,
                submittedOn: quoteLine.submittedOn,
                poNumber: quoteLine.referenceNumber,
                status: quoteLine.quoteStatus,
                quoteNumber: `${quoteLine.orderId} ${quoteLine.orderType}`,
                orderId: quoteLine.orderId,
                orderType: quoteLine.orderType,
                convertedToOrder: quoteLine.convertedToOrder,
            });
        });
        setRows(rows);
    };

    const hideConfirmation = () => {
        setConfirmationOptions({ show: false });
    };


    const refetchQuotes = async () => {
          if (typeof props.fetchOrders === 'function') {
                await Promise.resolve(props.fetchOrders());
              } else {
                getQuotes();
              }
    };

    const handleRejectClick = async (documentNumber) => {
        hideConfirmation();
        setRejecting(true);
        setRejectingDocument(documentNumber);
        props.setFetching(true);
        try {
            const response = await QuoteService.rejectQuote(
                documentNumber,
                documentCompany,
                customerId,
                implementationId
            );
            if (!response) {
                Notifications.error(props.localized.QuoteNotRejected);
            }
            Notifications.message(props.localized.QuoteRejected);
            await refetchQuotes();
            if (drawerOpen && selectedRow?.orderId === documentNumber) {
                closeDrawer()
            };
        }
        catch (err) {
            Notifications.error(props.localized.QuoteNotRejected);
        }
        finally {
            setRejecting(false);
            setRejectingDocument(null);
            props.setFetching(false);
        }
    };

    const rejectQuoteConfirmation = (documentNumber) => {
        setConfirmationOptions(prevObject => ({
            ...prevObject,
            show: true,
            message: props.localized.QuoteRejectWarning,
            callback: () => {
                handleRejectClick(documentNumber);
            },
        }));
    }

    const editQuote = (quoteId) => {
        let key = `${quoteId}-${documentCompany}000SQ`;
        props.history.push(`/quote/edit/${key}`);
    };

    const handleConvertToOrderConfirmation = async (form)=> {
        var convertQuoteToOrderData = {
            documentNumber: form.orderId,
            documentCompany: form.orderCompany,
            customerPO: form.poNumber,
            comments: form.comments,
            customerId: customerId,
            implementationId: implementationId
        };
        hideConfirmation();
        setConvertingQuote(true);
        setDocumentNumber(form.orderId);

        await QuoteService.convertQuoteToOrder(convertQuoteToOrderData).then((res) => {
            if (res) {
                Notifications.message(props.localized.YourQuoteIsBeingConverted);
                setDocumentNumber(res.documentNumber);
                setConvertingQuote(false);
                setConvertedQuote(true);
                props.hideQuotesFilter(true);
                props.history.push({
                    pathname: "/cart/confirmation",
                    state: { IsQuote: false, DocumentNumber: res.documentNumber },
                });
            } else {
                Notifications.error(
                    "Order could not be placed.Please contact sales representative"
                );
                setConvertingQuote(false);
            }
        });
    };

    const getComments = async (row) => {
        setConvertModalFetching(true);
        try {
            const res = await Api.fetch(
                `/api/orders/getCommentsBySale/${row.orderId}/${documentCompany}/${row.orderType}`
            );

            const nextComments = res || "";
            setSelectedRow({ ...row, comments: nextComments });
            setConvertComment(nextComments);

            const nextPO = row.poNumber ?? "";
            setConvertPO(nextPO);

            setOpenConvertModal(true);
        } finally {
            setConvertModalFetching(false);
        }
    };

    const convertQuoteToOrder = async (form) => {
        props.setFetching(true);
        await handleConvertToOrderConfirmation(form);
        getQuotes();
    };

    const getPDF = (orderNumber, orderType) => {
        Api.downloadFileName(`/api/orders/GetPDF/${props.selectedCustomer.billToId}/${orderNumber}/${orderType}`,
            `${props.selectedCustomer.billToId}-${orderNumber}${orderType}`)
            .then(handleError);
    };

    const handleError = (res) => {
        if (!res) Notifications.error(localized.NoPDFMessage);
    };
    const handleClickOpenConvertModal = async (e, row) => {
        e.stopPropagation();
        await getComments(row);
        setSelectedRow(row);
        setOpenConvertModal(true);

    }

    const renderConvertModal = (row) => {
        if (!row) return null;
        const handleSubmitConvertModal = (event) => {
            event.preventDefault();
            const formData = new FormData(event.currentTarget);
            formData.append("id", row.id);
            formData.append("orderId", row.orderId);
            formData.append("orderCompany", documentCompany);
            const formJson = Object.fromEntries(formData.entries());
            convertQuoteToOrder(formJson);
            handleCloseConvertModal();
        };

 };

    const listViewCell = (params) => {
        const row = params.row;
        const isThisRowOpen = drawerOpen && selectedRow?.id === row.id;
        

        const statusText = StatusHelper.formatStatus(row.status, props.localized);

        return (
            <Box sx={{ width: '100%', pt: 1, pb: 0 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    <Typography
                        variant="body1"
                        fontWeight={700}
                        component={Link}
                        to={`/quote/${row.orderId}/${documentCompany}/${row.orderType}`}
                        style={{ textDecoration: 'none' }}
                    >
                        {row.orderId}
                    </Typography>
                    <Chip size="small" label={statusText} />                   
                        <strong>
                            <div className="br-invoice-pdf d-inline-flex" style={{ fontSize: '18px' }}>
                                <div
                                    style={{
                                        cursor: 'pointer' ,
                                        paddingRight: '14px',
                                        opacity: 1 
                                    }}
                                    onClick={() => {getPDF(params.row.orderId, params.row.orderType);}}
                                    title={localized.DownloadPDF}
                                >
                                    <PDFIcon />
                                </div>
                            </div>
                        </strong>
                </Stack>
                
                <Typography
                    variant="body2"
                    sx={{ mt: 0.25, color: 'text.secondary' }}
                >
                    <strong>{props.localized.QuoteDate}:</strong>{' '}
                    <DateDisplay date={row.submittedOn} dateFormat={props.dateFormat} />
                </Typography>

                <Stack
                    direction="row"
                    spacing={2}
                    sx={{ mt: 0.5 }}
                    alignItems="center"
                >

                    <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation?.(); openDrawer(row); }}
                        aria-label={props.localized?.ViewDetails || 'View details'}
                        title={props.localized?.ViewDetails || 'View details'}
                        sx={{ p: 0, color: "black", flexShrink: 0 }}
                    >
                        <ExpandMoreIcon
                            fontSize="small"
                            sx={{
                                p: 0,
                                transition: 'transform 200ms ease',
                                transform: isThisRowOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            }}
                        />
                    </IconButton>
                </Stack>
            </Box>
        );
    };



    const MobileDetailsDrawer = () => {
        const row = selectedRow;
        if (!row) {
            return null;
        }
        else {
            return (
                <SwipeableDrawer
                    anchor="bottom"
                    open={drawerOpen}
                    onOpen={() => setDrawerOpen(true)}
                    onClose={closeDrawer}                    
                    PaperProps={{
                        sx: {
                            borderTopLeftRadius: 12,
                            borderTopRightRadius: 12,
                            pb: 2,
                            pointerEvents: isAnyModalOpen ? 'none' : 'auto'
                        }
                    }}
                    ModalProps={{
                        hideBackdrop: isAnyModalOpen,
                        keepMounted: true,
                        disableEnforceFocus: true,
                        disableAutoFocus: true,
                        sx:{ pointerEvents: isAnyModalOpen ? 'none' : 'auto' }
                    }}
                    transitionDuration={280}
                    slotProps={{
                        transition: {
                            timeout: 280
                        },
                    }}

                >
                    <Fade in={drawerOpen} timeout={{ enter: 250, exit: 250 }} mountOnEnter unmountOnExit>
                        <Box sx={{ p: 2 }}>
                            <Box
                                sx={{
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 1,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    bgcolor: 'background.paper',
                                    pb: 1
                                }}
                            >
                                <IconButton
                                    size="small"
                                    onClick={closeDrawer}
                                    aria-label={props.localized?.CollapseDetails || 'Collapse details'}
                                    title={props.localized?.CollapseDetails || 'Collapse details'}
                                    sx={{
                                        transition: 'transform 120ms ease',
                                        '&:active': { transform: 'scale(0.96)' },
                                        p: 0
                                    }}
                                >
                                    <ExpandMoreIcon fontSize="small" sx={{ p: 0 }} />
                                </IconButton>
                            </Box>
                            <Stack direction="row" alignItems="baseline" justifyContent="space-between">
                                <Typography variant="h6" fontWeight={700}>
                                    {props.localized.QuoteNumber}: {row.orderId}
                                </Typography>
                                <Chip size="small" label={StatusHelper.formatStatus(row.status, props.localized)} />
                            </Stack>

                            <Divider sx={{ my: 1.5 }} />
                            <Stack spacing={1.25}>
                                <Box>
                                    <Typography variant="subtitle2">{props.localized.ReferenceNumber}</Typography>
                                    <Typography variant="body2">{row.poNumber || '-'}</Typography>
                                </Box>

                                <Stack
                                    direction="row"
                                    sx={{
                                        justifyContent: "space-between"
                                    }}
                                    flexWrap="wrap" >
                                    <Box>
                                        <Typography variant="subtitle2">{props.localized.OrderDate}</Typography>
                                        <DateDisplay date={row.submittedOn} dateFormat={props.dateFormat} />
                                    </Box>
                                </Stack>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{ pt: 1, flexWrap: 'wrap', rowGap: 1 }}
                                >
                                    {props.downloadQuotePDF && (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={<PDFIcon />}
                                            onClick={() => getPDF(row.orderId, row.orderType)}
                                            disabled={ isAnyModalOpen || props.fetching || rejecting || convertingQuote}
                                        >
                                            {localized.DownloadPDF}
                                        </Button>
                                    )}
                                    {props.rejectQuoteEnabled && (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            color="error"
                                            onClick={() => {
                                                rejectQuoteConfirmation(row.orderId)
                                            }}
                                            disabled={row.convertedToOrder || isAnyModalOpen || props.fetching || rejecting || convertingQuote}
                                        >
                                            {localized.Reject}
                                        </Button>
                                    )}                        
                                    {props.convertQuoteEnabled && (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            color="update"
                                            onClick={(e) => handleClickOpenConvertModal(e, row)}
                                            disabled={row.convertedToOrder || isAnyModalOpen || props.fetching || rejecting || convertingQuote}
                                        >
                                            {localized.ConvertToOrder}
                                        </Button>
                                    )}
                                    {props.editQuoteEnabled && (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            color="update"
                                            onClick={() => editQuote(row.orderId)}
                                            disabled={row.convertedToOrder || isAnyModalOpen || props.fetching || rejecting || convertingQuote}
                                        >
                                            {localized.EditQuote}
                                        </Button>
                                    )}
                                </Stack>
                            </Stack>
                        </Box>
                    </Fade>
                </SwipeableDrawer>
            );
        }
    };

    const listViewColDef = {
        field: 'listColumn',
        headerName: props.localized.QuoteNumber,
        flex: 1,
        sortable: false,
        disableColumnMenu: true,
        renderCell: listViewCell
    };

    const columns = [
        {
            field: 'submittedOn',
            headerName: props.localized.QuoteDate,
            headerAlign: 'left',
            renderCell: (params) => {
                return (
                    <DateDisplay date={params.row.submittedOn} dateFormat={props.dateFormat} />
                )
            },
        },
        {
            field: 'quoteNumber',
            headerName: props.localized.QuoteNumber,
            headerAlign: 'left',
            flex: 0.2,
            renderCell: (params) => (
                <Link
                    to= {`/quote/${params.row.orderId}/${documentCompany}/${params.row.orderType}`}
                    style={ { textDecoration: 'none' } }
                >
                    { params.row.quoteNumber }
                </Link >
            )
        },
        {
            field: 'status',
            headerName: localized.Status,
            headerAlign: 'left',
            renderCell: (params) => {
                return (
                    <div>{StatusHelper.formatQuoteStatus(params.row.status, localized)}</div>
                )
            },
            flex: 0.1
        },
        {
            field: 'poNumber',
            headerName: localized.PONumber,
            headerAlign: 'left',
            renderCell: (params) => {
                { params.row.poNumber }
            },
            flex: 0.2
        },

        {
            field: 'pdf',
            headerName: 'PDF',
            headerAlign: 'center',
            align: 'center',
            width: 72,
            sortable: false,
            disableColumnMenu: true,
            disableExport: true,
            renderCell: (params) => (
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                    <div
                        style={{ cursor: 'pointer' }}
                        onClick={() => { getPDF(params.row.orderId, params.row.orderType); }}
                        title={localized.DownloadPDF}
                    >
                        <PDFIcon />
                    </div>
                </Box>
            )
        },
        {
            field: 'reject',
            headerName: localized.Reject,
            headerAlign: 'center',
            align: 'center',
            minWidth: 110,            
            flex: 0.12,               
            sortable: false,
            disableColumnMenu: true,
            disableExport: true,
            renderCell: (params) => (
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                    {props.rejectQuoteEnabled && (
                        <Button
                            variant="standard"
                            color="update"
                            onClick={() =>
                                rejectQuoteConfirmation(params.row.orderId)
                            }
                            disabled={params.row.convertedToOrder || rejecting}
                            sx={{ whiteSpace: 'nowrap' }}     // NO wrap → avoids truncation
                        >
                            {localized.Reject}
                        </Button>
                    )}
                </Box>
            ),
            disableClickEventBubbling: true
        },


        {
            field: 'convert',
            headerName: localized.ConvertToOrder,
            headerAlign: 'center',
            align: 'center',
            minWidth: 180,            // fits “Convert to Order” without wrap
            flex: 0.18,
            sortable: false,
            disableColumnMenu: true,
            disableExport: true,
            renderCell: (params) => (
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                    {props.convertQuoteEnabled && (
                        <Button
                            variant="standard"
                            color="update"
                            onClick={(e) => handleClickOpenConvertModal(e, params.row)}
                            disabled={params.row.convertedToOrder}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            {(convertingQuote && documentNumber === params.row.orderId)
                                ? <CircularProgress sx={{ color: 'white' }} size={24} />
                                : localized.ConvertToOrder}
                        </Button>
                    )}
                </Box>
            ),
        },
        {
            field: 'edit',
            headerName: localized.EditQuote,
            headerAlign: 'center',
            align: 'center',
            minWidth: 140,          
            flex: 0.14,
            sortable: false,
            disableColumnMenu: true,
            disableExport: true,
            renderCell: (params) => (
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                    {props.editQuoteEnabled && (
                        <Button
                            variant="standard"
                            color="update"
                            disabled={params.row.convertedToOrder}
                            onClick={() => editQuote(params.row.orderId)}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            {localized.EditQuote}
                        </Button>
                    )}
                </Box>
            ),
        }

    ];


    return (
        <>  
            {
                (!convertedQuote && (gridLocalized && Object.keys(gridLocalized).length > 0)) &&
                <>
                    <ThemeProvider theme={wrapperTheme}>
                        <Paper sx={{
                            height: 700,
                            width: '100%'
                        }}>
                            <TranslatableDataGridServerLoaded
                                rows={rows}
                                columns={columns}
                                count={props.count}
                                toolbar={customToolbar}
                                loading={props.fetching || convertingQuote || rejecting}
                                fetching={props.fetching || convertingQuote || rejecting}
                                labels={gridLocalized}
                                square={true}
                                listView={props.listView}
                                listViewColumn={listViewColDef}
                                paginationModel={props.paginationModel}
                                setPaginationModel={props.setPaginationModel}
                                getRowHeight={props.listView ? () => 'auto' : undefined}
                                getEstimatedRowHeight={props.listView ? () => 180 : undefined}
                            />
                            {props.listView && <MobileDetailsDrawer />}
                        </Paper>
                    </ThemeProvider>
                    <Portal>
                    {confirmationOptions.show && (

                                 <Box sx={{ zIndex: theme => theme.zIndex.modal + 2, position: 'relative' }}>
                                       <ModalPopup {...confirmationOptions} onHide={hideConfirmation} />
                                 </Box>


                        )}
                    </Portal>                   
                    <ConvertToOrderModal
                        show={openConvertModal}
                        onHide={handleCloseConvertModal}
                        fetching={convertModalFetching}
                        poNumber={convertPO}
                        comments={convertComment}
                        poNumberUpdated={poNumberUpdated}
                        commentsUpdated={commentsUpdated}
                        requiredPO={requiredPO}
                        maxPOLength={props.storefrontConfig.settingsList.MaxPOLength}
                        localized={localized}
                        converting={convertingQuote}
                        callback={async () => {
                            if (!selectedRow) return;
                            handleCloseConvertModal();
                            const form = {
                                orderId: selectedRow.orderId,
                                orderCompany: documentCompany,
                                poNumber: convertPO,
                                comments: convertComment,
                            };
                            await convertQuoteToOrder(form);
                        }}
                    />
                </>
            }
        </>
    );
};
export default withRouter(QuoteLines);